import React, { useState, useEffect, useCallback } from "react";
import Layout from "../../Layout";
import { useParams, useNavigate, Link } from "react-router-dom";
import { BiChevronLeft, BiX } from "react-icons/bi";
import { FaStar, FaRegStar } from "react-icons/fa";
import { toast } from "react-hot-toast";
import axios from "axios";
import { BaseUrl } from "../../BaseUrl.js";

function OrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  // Rating modal state
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingStars, setRatingStars] = useState(0);
  const [hoverStars, setHoverStars] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [submittingRating, setSubmittingRating] = useState(false);

  const fetchOrderDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BaseUrl}/facility/orders/${id}`, {
        withCredentials: true,
      });

      console.log("Order Details Response:", response.data);
      setOrder(response.data?.data || response.data);
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error("Error loading order details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchOrderDetails();
    }
  }, [id, fetchOrderDetails]);

  // Submit rating
  const handleSubmitRating = async () => {
    if (ratingStars === 0) {
      toast.error("Please select a rating");
      return;
    }

    setSubmittingRating(true);
    try {
      const response = await axios.post(
        `${BaseUrl}/facility/orders/${id}/rate`,
        {
          stars: ratingStars,
          feedback: feedback,
        },
        { withCredentials: true }
      );

      if (response.data?.success) {
        toast.success("Thank you for your feedback!");
        setShowRatingModal(false);
        // Update local order state with rating
        setOrder((prev) => ({
          ...prev,
          deliveryRating: {
            stars: ratingStars,
            feedback: feedback,
            ratedAt: new Date(),
          },
        }));
      }
    } catch (error) {
      console.error("Error submitting rating:", error);
      toast.error("Failed to submit rating");
    } finally {
      setSubmittingRating(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className='flex items-center justify-center py-20'>
          <div className='h-10 w-10 animate-spin rounded-full border-b-2 border-subMain'></div>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div className='flex flex-col items-center justify-center py-20'>
          <p className='text-lg text-textGray'>Order not found</p>
          <button
            onClick={() => navigate("/orders")}
            className='mt-4 rounded-lg bg-subMain px-6 py-2 text-white'
          >
            Back to Orders
          </button>
        </div>
      </Layout>
    );
  }

  // Extract data
  const medications = order.medications || [];
  const orderId =
    order.orderId || `ORD${String(order._id).slice(-8).toUpperCase()}`;
  const deliveryAddress =
    order.deliveryAddress ||
    order.patientId?.address?.fullAddress ||
    order.address ||
    "Address not available";
  const orderDate = order.createdAt
    ? new Date(order.createdAt).toLocaleString("en-IN", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "N/A";

  // Calculate bill
  const mrp =
    order.totalAmount ||
    medications.reduce((sum, med) => sum + (med.price || med.mrp || 0), 0);
  const discount = order.discount || 0;
  const itemTotal = mrp - discount;
  const handlingCharge = order.handlingCharge || 2;
  const deliveryCharge = order.deliveryCharge || 0;
  const donation = order.donation || 1;
  const billTotal = itemTotal + handlingCharge + deliveryCharge + donation;

  // Check if already rated
  const isRated = order.deliveryRating?.stars > 0;

  return (
    <Layout>
      {/* Rating Modal */}
      {showRatingModal && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'>
          <div className='w-full max-w-md rounded-xl bg-white p-6 shadow-lg'>
            <div className='mb-4 flex items-center justify-between'>
              <h2 className='text-lg font-semibold'>Rate Delivery Service</h2>
              <button
                onClick={() => setShowRatingModal(false)}
                className='text-textGray hover:text-main'
              >
                <BiX className='text-2xl' />
              </button>
            </div>

            {/* Star Rating */}
            <div className='mb-6'>
              <p className='mb-3 text-sm text-textGray'>
                How was your delivery experience?
              </p>
              <div className='flex justify-center gap-2'>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRatingStars(star)}
                    onMouseEnter={() => setHoverStars(star)}
                    onMouseLeave={() => setHoverStars(0)}
                    className='transition-transform hover:scale-110'
                  >
                    {(hoverStars || ratingStars) >= star ? (
                      <FaStar className='text-3xl text-yellow-400' />
                    ) : (
                      <FaRegStar className='text-3xl text-gray-300' />
                    )}
                  </button>
                ))}
              </div>
              {ratingStars > 0 && (
                <p className='mt-2 text-center text-sm text-textGray'>
                  {ratingStars === 1 && "Poor"}
                  {ratingStars === 2 && "Fair"}
                  {ratingStars === 3 && "Good"}
                  {ratingStars === 4 && "Very Good"}
                  {ratingStars === 5 && "Excellent!"}
                </p>
              )}
            </div>

            {/* Feedback */}
            <div className='mb-6'>
              <label className='mb-2 block text-sm font-medium text-textGray'>
                Additional Feedback (Optional)
              </label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder='Tell us more about your experience...'
                className='h-24 w-full resize-none rounded-lg border border-border bg-dry px-4 py-3 text-sm focus:border-subMain focus:outline-none'
              />
            </div>

            {/* Submit Button */}
            <div className='flex gap-3'>
              <button
                onClick={() => setShowRatingModal(false)}
                className='flex-1 rounded-lg border border-border py-3 text-sm font-medium text-main hover:bg-dry'
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitRating}
                disabled={submittingRating || ratingStars === 0}
                className='flex-1 rounded-lg bg-subMain py-3 text-sm font-medium text-white hover:bg-opacity-90 disabled:opacity-50'
              >
                {submittingRating ? "Submitting..." : "Submit Rating"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className='mb-6 flex items-center gap-4'>
        <button
          onClick={() => navigate("/orders")}
          className='flex h-10 w-10 items-center justify-center rounded-lg border border-border hover:bg-dry'
        >
          <BiChevronLeft className='text-xl' />
        </button>
        <h1 className='text-xl font-semibold'>Order Details</h1>
      </div>

      {/* Items in Order */}
      <div className='mb-6 rounded-xl border border-border bg-white p-6'>
        <div className='mb-6 flex items-center justify-between'>
          <h2 className='text-lg font-semibold'>Items in this Order</h2>
          {order.prescriptionId && (
            <Link
              to={`/prescriptiondetail?id=${order.prescriptionId}`}
              className='text-sm text-subMain hover:underline'
            >
              Attached Prescription
            </Link>
          )}
        </div>

        {/* Medications Table */}
        <div className='overflow-x-auto'>
          <table className='w-full'>
            <thead className='border-b border-border'>
              <tr>
                <th className='pb-4 text-left text-xs font-semibold text-textGray'>
                  #
                </th>
                <th className='pb-4 text-left text-xs font-semibold text-textGray'>
                  Medicine
                </th>
                <th className='pb-4 text-left text-xs font-semibold text-textGray'>
                  Medications
                </th>
                <th className='pb-4 text-left text-xs font-semibold text-textGray'>
                  Frequency
                </th>
                <th className='pb-4 text-left text-xs font-semibold text-textGray'>
                  Duration
                </th>
                <th className='pb-4 text-left text-xs font-semibold text-textGray'>
                  Remarks
                </th>
                <th className='pb-4 text-right text-xs font-semibold text-textGray'>
                  Rs.
                </th>
              </tr>
            </thead>
            <tbody>
              {medications.length > 0 ? (
                medications.map((med, index) => (
                  <tr
                    key={index}
                    className='border-b border-border last:border-0'
                  >
                    <td className='py-4 text-sm'>{index + 1}</td>
                    <td className='py-4'>
                      <div className='flex items-center gap-3'>
                        <div className='flex h-16 w-16 items-center justify-center rounded-lg bg-gray-100'>
                          {med.image ? (
                            <img
                              src={med.image}
                              alt={med.name}
                              className='h-12 w-12 object-contain'
                            />
                          ) : (
                            <span className='text-xs text-textGray'>
                              No Image
                            </span>
                          )}
                        </div>
                        <div>
                          <p className='text-sm font-medium'>
                            {med.name || med.medicine || "Medicine"}
                          </p>
                          <p className='text-xs text-textGray'>
                            Dosage: {med.composition || med.dosage || "N/A"}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className='py-4 text-sm text-textGray'>
                      {med.instruction ||
                        med.instructions ||
                        med.medication ||
                        "As prescribed"}
                    </td>
                    <td className='py-4 text-sm text-textGray'>
                      {med.frequency || "1-1-1"}
                    </td>
                    <td className='py-4 text-sm text-textGray'>
                      {med.duration || "30 Days"}
                    </td>
                    <td className='max-w-[150px] truncate py-4 text-sm text-textGray'>
                      {med.remarks || med.notes || "N/A"}
                    </td>
                    <td className='py-4 text-right text-sm font-medium'>
                      {(med.price || med.mrp || 0).toFixed(2)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className='py-8 text-center text-textGray'>
                    No medications in this order
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Feedback Section */}
        <div className='mt-6 flex items-center justify-between rounded-lg bg-dry p-4'>
          <div>
            <p className='text-sm font-medium'>How were your ordered items?</p>
            <p className='text-xs text-textGray'>
              {isRated
                ? `You rated this ${order.deliveryRating.stars} star${order.deliveryRating.stars > 1 ? "s" : ""}`
                : "Your feedback helps us improve!"}
            </p>
          </div>
          {isRated ? (
            <div className='flex items-center gap-1'>
              {[1, 2, 3, 4, 5].map((star) => (
                <FaStar
                  key={star}
                  className={`text-lg ${
                    star <= order.deliveryRating.stars
                      ? "text-yellow-400"
                      : "text-gray-300"
                  }`}
                />
              ))}
            </div>
          ) : (
            <button
              onClick={() => setShowRatingModal(true)}
              className='rounded-lg bg-subMain px-6 py-2 text-sm font-medium text-white'
            >
              Rate Now
            </button>
          )}
        </div>
      </div>

      {/* Order Info */}
      <div className='mb-6 grid grid-cols-1 gap-6 md:grid-cols-2'>
        <div className='rounded-xl border border-border bg-white p-6'>
          <div className='space-y-4'>
            <div>
              <p className='text-xs text-textGray'>Order ID</p>
              <p className='text-sm font-medium'>{orderId}</p>
            </div>
            <div>
              <p className='text-xs text-textGray'>Delivery Address</p>
              <p className='text-sm font-medium'>{deliveryAddress}</p>
            </div>
          </div>
        </div>
        <div className='rounded-xl border border-border bg-white p-6'>
          <div className='space-y-4'>
            <div>
              <p className='text-xs text-textGray'>Payment method</p>
              <p className='text-sm font-medium'>
                {order.paymentMethod || "Pay on Delivery"}
              </p>
            </div>
            <div>
              <p className='text-xs text-textGray'>Order Placed On</p>
              <p className='text-sm font-medium'>{orderDate}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bill Details */}
      <div className='rounded-xl border border-border bg-white p-6'>
        <h3 className='mb-4 text-lg font-semibold'>Bill Details</h3>
        <div className='space-y-3'>
          <div className='flex justify-between text-sm'>
            <span className='text-textGray'>MRP</span>
            <span>Rs {mrp.toFixed(2)}</span>
          </div>
          <div className='flex justify-between text-sm'>
            <span className='text-textGray'>Product Discount</span>
            <span className='text-red-500'>-Rs {discount.toFixed(2)}</span>
          </div>
          <div className='flex justify-between text-sm'>
            <span className='text-textGray'>Item Total</span>
            <span>Rs {itemTotal.toFixed(2)}</span>
          </div>
          <div className='flex justify-between text-sm'>
            <span className='text-textGray'>Handling Charge</span>
            <span className='text-subMain'>
              +Rs {handlingCharge.toFixed(2)}
            </span>
          </div>
          <div className='flex justify-between text-sm'>
            <span className='text-textGray'>Delivery Charge</span>
            <span className='text-green-500'>
              {deliveryCharge === 0
                ? "Free"
                : `Rs ${deliveryCharge.toFixed(2)}`}
            </span>
          </div>
          <div className='flex justify-between text-sm'>
            <span className='text-textGray'>Feeding India Donation</span>
            <span className='text-subMain'>+Rs {donation.toFixed(2)}</span>
          </div>
          <div className='mt-3 border-t border-border pt-3'>
            <div className='flex justify-between'>
              <span className='text-sm font-semibold text-red-500'>
                Bill Total
              </span>
              <span className='text-lg font-bold text-subMain'>
                Rs {billTotal.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default OrderDetails;
